type FetchResponse = {
  ok: boolean
  status?: number
  json: () => Promise<unknown>
}

type Fetcher = (input: string, init: RequestInit) => Promise<FetchResponse>
const TERMS_CHANGED = 'The Terms have changed. Reload the Terms and review them again.'
const allowedErrors = new Set([
  TERMS_CHANGED,
  'Registration failed.',
  'Please review the registration fields.',
  'Valid email, password, and Terms acceptance are required.',
  'Registration is temporarily unavailable. Please try again.',
  'Google sign-in is temporarily unavailable.'
])
function safeError(error: unknown) {
  return error instanceof Error && allowedErrors.has(error.message)
    ? error.message
    : 'Authentication is temporarily unavailable. Please try again.'
}

function objectPayload(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null
}

export async function submitEmailRegistration(
  fetcher: Fetcher,
  input: { email: string; password: string; termsAccepted: true; termsVersion: string }
) {
  const response = await fetcher('/api/auth/register', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input)
  }).catch(() => {
    throw new Error('Registration is temporarily unavailable. Please try again.')
  })
  if (response.status === 409) throw new Error(TERMS_CHANGED)
  const payload = objectPayload(await response.json().catch(() => null))
  if (!response.ok || payload?.success !== true || payload.status !== 'confirmation_required') {
    const message =
      typeof payload?.error === 'string' && allowedErrors.has(payload.error)
        ? payload.error
        : 'Registration failed.'
    throw new Error(message)
  }
  return { status: 'confirmation_required' as const }
}

export async function requestGoogleOAuthStart(
  fetcher: Fetcher,
  input: { intent: 'login' } | { intent: 'registration'; termsAccepted: true; termsVersion: string }
) {
  const response = await fetcher('/api/auth/oauth/start', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(input)
  }).catch(() => {
    throw new Error('Google sign-in is temporarily unavailable.')
  })
  if (input.intent === 'registration' && response.status === 409) throw new Error(TERMS_CHANGED)
  const payload = objectPayload(await response.json().catch(() => null))
  if (!response.ok || typeof payload?.url !== 'string' || !payload.url.startsWith('https://')) {
    throw new Error('Google sign-in is temporarily unavailable.')
  }
  return payload.url
}

export function createAuthJourneyScope(actorKey: string) {
  let active = false
  let generation = 0
  let pending = false
  return {
    activate(currentActorKey: string) {
      active = actorKey === currentActorKey
      generation += 1
      pending = false
    },
    dispose() {
      active = false
      generation += 1
      pending = false
    },
    async run<T>(callbacks: {
      request: (isCurrent: () => boolean) => Promise<T>
      onSuccess: (result: T) => void
      onState: (state: { pending: boolean; error: string | null }) => void
    }) {
      if (!active || pending) return
      pending = true
      const requestGeneration = generation
      const isCurrent = () => active && requestGeneration === generation
      callbacks.onState({ pending: true, error: null })
      let error: string | null = null
      try {
        const result = await callbacks.request(isCurrent)
        if (isCurrent()) callbacks.onSuccess(result)
      } catch (failure) {
        error = safeError(failure)
      } finally {
        if (isCurrent()) {
          pending = false
          callbacks.onState({ pending: false, error })
        }
      }
    }
  }
}
